import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Activity,
  FileUp,
  FilePlus,
  CheckCircle,
  XCircle,
  MessageSquare,
  Mail,
  StickyNote,
  RefreshCw,
  ChevronDown,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// Activity type to icon mapping
const getActivityIcon = (activityType) => {
  switch (activityType) {
    case "file_uploaded":
    case "file_replaced":
      return <FileUp className="w-4 h-4" />;
    case "version_created":
      return <FilePlus className="w-4 h-4" />;
    case "version_approved":
    case "file_approved":
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    case "version_rejected":
    case "file_rejected":
      return <XCircle className="w-4 h-4 text-red-600" />;
    case "comment_added":
    case "comment_replied":
      return <MessageSquare className="w-4 h-4 text-blue-600" />;
    case "email_sent":
    case "email_received":
      return <Mail className="w-4 h-4 text-purple-600" />;
    case "note_added":
      return <StickyNote className="w-4 h-4 text-yellow-600" />;
    default:
      return <Activity className="w-4 h-4 text-gray-600" />;
  }
};

// Activity type to color mapping
const getActivityColor = (activityType) => {
  if (activityType.includes("approved")) return "border-l-green-500 bg-green-50";
  if (activityType.includes("rejected")) return "border-l-red-500 bg-red-50";
  if (activityType.includes("comment")) return "border-l-blue-500 bg-blue-50";
  if (activityType.includes("email")) return "border-l-purple-500 bg-purple-50";
  if (activityType.includes("note")) return "border-l-yellow-500 bg-yellow-50";
  if (activityType.includes("version") || activityType.includes("file"))
    return "border-l-slate-500 bg-slate-50";
  return "border-l-gray-400 bg-gray-50";
};

function ActivityFeed({ projectId, projectName }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const LIMIT = 20;

  useEffect(() => {
    fetchActivities(true);
  }, [projectId]);

  const fetchActivities = async (reset = false) => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const currentOffset = reset ? 0 : offset;
      const response = await axios.get(
        `${API}/projects/${projectId}/activity?limit=${LIMIT}&offset=${currentOffset}`,
        getAuthHeaders()
      );
      
      const newActivities = response.data.activities || [];
      
      if (reset) {
        setActivities(newActivities);
        setOffset(LIMIT);
      } else {
        setActivities([...activities, ...newActivities]);
        setOffset(currentOffset + LIMIT);
      }
      
      setTotal(response.data.total);
      setHasMore(currentOffset + LIMIT < response.data.total);
    } catch (error) {
      console.error("Failed to load activities:", error);
      toast.error("Failed to load activity feed");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteContent.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/activity/note`,
        { content: noteContent },
        getAuthHeaders()
      );
      setNoteContent("");
      setShowNoteInput(false);
      fetchActivities(true);
      toast.success("Note added to activity feed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    // Within 24 hours, show relative time
    if (diff < 86400000) {
      if (diff < 60000) return "Just now";
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      return `${Math.floor(diff / 3600000)}h ago`;
    }
    
    // Otherwise show date
    return date.toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const groupActivitiesByDate = (activities) => {
    const groups = {};
    
    activities.forEach((activity) => {
      const date = new Date(activity.created_at);
      const dateKey = date.toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(activity);
    });
    
    return groups;
  };

  const groupedActivities = groupActivitiesByDate(activities);

  return (
    <div className="activity-feed" data-testid="activity-feed">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-800">Activity Timeline</h3>
          <span className="text-sm text-gray-500">({total} events)</span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchActivities(true)}
            disabled={loading}
            data-testid="refresh-activity-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNoteInput(!showNoteInput)}
            data-testid="add-note-btn"
          >
            <StickyNote className="w-4 h-4 mr-1" />
            Add Note
          </Button>
        </div>
      </div>

      {/* Add Note Form */}
      {showNoteInput && (
        <form onSubmit={handleAddNote} className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <Textarea
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            placeholder="Add a note to the activity timeline..."
            rows={2}
            className="mb-2 bg-white"
            data-testid="note-input"
          />
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={submitting || !noteContent.trim()}
              data-testid="submit-note-btn"
            >
              Add Note
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowNoteInput(false);
                setNoteContent("");
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Activity Timeline */}
      {loading && activities.length === 0 ? (
        <div className="text-center py-8 text-gray-500" data-testid="loading-activities">
          Loading activity feed...
        </div>
      ) : activities.length === 0 ? (
        <div className="text-center py-8 text-gray-500" data-testid="no-activities">
          No activity recorded yet.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedActivities).map(([dateKey, dateActivities]) => (
            <div key={dateKey}>
              {/* Date Header */}
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-xs font-medium text-gray-500 px-2">
                  {dateKey}
                </span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>

              {/* Activities for this date */}
              <div className="space-y-2">
                {dateActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className={`activity-item flex gap-3 p-3 rounded-lg border-l-4 ${getActivityColor(
                      activity.activity_type
                    )}`}
                    data-testid={`activity-${activity.id}`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {getActivityIcon(activity.activity_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800">{activity.summary}</p>
                      {activity.metadata?.remarks && (
                        <p className="text-xs text-gray-600 mt-1 italic">
                          "{activity.metadata.remarks}"
                        </p>
                      )}
                      {activity.metadata?.content_preview && (
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          "{activity.metadata.content_preview}"
                        </p>
                      )}
                      {activity.metadata?.full_content && activity.activity_type === "note_added" && (
                        <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                          {activity.metadata.full_content}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-xs text-gray-500">
                      {formatDate(activity.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Load More */}
          {hasMore && (
            <div className="text-center pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchActivities(false)}
                disabled={loading}
                data-testid="load-more-btn"
              >
                <ChevronDown className="w-4 h-4 mr-1" />
                Load More ({total - activities.length} remaining)
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ActivityFeed;
